/**
 * Spec 087 — Coverage Analysis Detail Page
 *
 * Renders a single SitemapAnalysis with in-flight phase display, URL
 * inventory, gap recommendations (with "cluster stale" banners), and a
 * cancel button for non-terminal analyses.
 */
import { useEffect, useState, useCallback, Fragment } from 'react';
import type { ReactElement } from 'react';
import { useParams, Link } from 'react-router';
import { apiGet, apiFetch } from '../../lib/api.js';
import { ScoreCard } from '../../components/ScoreCard.js';
import { EmptyState } from '../../components/EmptyState.js';
import { UrlInventoryTable } from '../../components/coverage/UrlInventoryTable.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { StallDiagnostics } from '../../components/StallDiagnostics.js';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { Region, RegionHead } from '../../components/ui/Region.js';

interface AnalysisDetail {
  id: string;
  brand_id: string;
  sitemap_url: string;
  phase: string;
  status: string;
  scores: {
    coverage_score: number | null;
    quality_score: number | null;
    topic_distribution_score: number | null;
    banner: string | null;
  };
  total_urls_in_sitemap: number;
  urls_triaged_tier_1: number;
  urls_triaged_tier_2: number;
  urls_triaged_tier_3: number;
  urls_triaged_tier_4: number;
  /** Spec 089c — count of URLs promoted to tier_1 by the GSC ground-truth post-pass. */
  gsc_upgraded_to_tier_1?: number;
  urls_evaluated: number;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  /** Spec 209 — reason a completed run stopped before covering every cluster. */
  partial_completion_reason?: string | null;
  job: {
    attempt_count: number;
    last_error: string | null;
    locked_until: string;
    updated_at: string;
  } | null;
}

/**
 * What kind of action a recommendation proposes, in plain language. The
 * wording matches the verbs already used in the generated playbook titles,
 * so the chip can never contradict the title the same consultant reads in
 * the Playbook view. The fallback keeps a stale client from breaking if a
 * new kind is added server-side.
 */
const RECOMMENDATION_KIND_LABEL: Record<string, string> = {
  expand_existing_page: 'Expand an existing page',
  restructure_existing: 'Restructure existing content',
  consolidate_thin_pages: 'Consolidate thin pages',
  new_dedicated_page: 'Create a new page',
  schema_or_metadata: 'Add schema or metadata',
};

function kindLabel(kind: string | null | undefined): string | null {
  if (!kind) return null;
  return RECOMMENDATION_KIND_LABEL[kind] ?? kind.replace(/_/g, ' ');
}

interface Recommendation {
  playbook_item_id: string;
  fanout_cluster_slug: string;
  gap_type: string;
  recommendation_kind: string;
  /** Meaningful only when the kind is a new dedicated page; null otherwise. */
  recommended_content_type: string | null;
  rationale: string;
  priority: string;
  estimated_coverage_gain: number;
  cluster_stale: boolean;
  playbook_id: string;
  playbook_title: string;
  playbook_status: string;
}

const TERMINAL = ['completed', 'failed', 'cancelled'];

export function CoverageAnalysisPage() {
  const { analysisId } = useParams();

  const [analysis, setAnalysis] = useState<AnalysisDetail | null>(null);
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    if (!analysisId) return;
    setLoading(true);
    setError(null);
    try {
      const a = await apiGet<{ data: AnalysisDetail }>(
        `/api/v1/sitemap-analyses/${analysisId}`,
      );
      setAnalysis(a.data);
      const r = await apiGet<{ data: Recommendation[] }>(
        `/api/v1/sitemap-analyses/${analysisId}/recommendations`,
      );
      setRecs(r.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  }, [analysisId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCancel = useCallback(async () => {
    if (!analysisId) return;
    setCancelling(true);
    try {
      await apiFetch(`/api/v1/sitemap-analyses/${analysisId}/cancel`, {
        method: 'POST',
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  }, [analysisId, load]);

  if (loading && !analysis) {
    return (
      <div className="flex items-center gap-2 text-slate-500">
        <Spinner  />
        Loading analysis…
      </div>
    );
  }
  if (!analysis) {
    return (
      <div>
        <EmptyState title="Analysis not found" description={error ?? 'Unknown id.'} />
      </div>
    );
  }

  const isTerminal = TERMINAL.includes(analysis.status);

  return (
    <div data-testid="coverage-analysis-detail" className="hub-page--dashboard" data-density="compact">
      <Region>
        <Breadcrumbs
          items={[
            { label: 'Coverage', to: '/coverage' },
            { label: `Analysis ${analysis.id.slice(0, 8)}` },
          ]}
        />
        <div className="hub-page__head mt-3">
          <div>
            <h1 className="hub-page__title">Analysis {analysis.id.slice(0, 8)}</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)] font-mono">{analysis.sitemap_url}</p>
          </div>
          {!isTerminal && (
            <button
              onClick={() => void handleCancel()}
              disabled={cancelling}
              data-testid="cancel-analysis"
              className="hub-btn hub-btn--ghost"
            >
              {cancelling ? <Spinner  /> : <Icon name="bx-x-circle" />}
              Cancel
            </button>
          )}
        </div>
      </Region>

      {/* Phase indicator */}
      <Region>
        <span className="font-medium text-[var(--hub-color-ink)]">Phase:</span>{' '}
        <span data-testid="phase" className="font-mono text-[var(--hub-color-ink)]">{analysis.phase}</span>
        {!isTerminal && (
          <Spinner className="ml-2 inline text-slate-400" />
        )}
      </Region>

      {/* Scores */}
      <Region>
        <ScoreCard
          coverage={analysis.scores.coverage_score}
          quality={analysis.scores.quality_score}
          topicDistribution={analysis.scores.topic_distribution_score}
          title="Content coverage scores"
          subtitle={`${analysis.urls_evaluated} evaluated of ${analysis.total_urls_in_sitemap} URLs in sitemap`}
        />
      </Region>

      {/* Tier breakdown */}
      <Region>
        <RegionHead title="URL triage" />
        <dl className="grid grid-cols-4 gap-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500">Tier 1 (critical)</dt>
            <dd className="tabular-nums">{analysis.urls_triaged_tier_1}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Tier 2 (relevant)</dt>
            <dd className="tabular-nums">{analysis.urls_triaged_tier_2}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Tier 3 (sampled)</dt>
            <dd className="tabular-nums">{analysis.urls_triaged_tier_3}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Tier 4 (skipped)</dt>
            <dd className="tabular-nums">{analysis.urls_triaged_tier_4}</dd>
          </div>
        </dl>
        {/* Spec 089c — GSC ground-truth tier-upgrade count. Only rendered
            when at least one URL was promoted; hidden otherwise (including
            for pre-089c analyses whose count defaults to 0). */}
        {(analysis.gsc_upgraded_to_tier_1 ?? 0) > 0 && (
          <p
            className="mt-3 text-xs text-teal-700"
            data-testid="coverage-gsc-upgrade-footer"
          >
            <span className="font-semibold">{analysis.gsc_upgraded_to_tier_1}</span>{' '}
            upgraded to tier_1 by GSC ground truth
          </p>
        )}
      </Region>

      {/* Per-URL inventory (gap 087-2) */}
      <Region>
        <UrlInventoryTable analysisId={analysis.id} />
      </Region>

      {/* Recommendations */}
      {recs.length > 0 && (
        <Region data-testid="gap-recommendations">
          <RegionHead title={`Content gap recommendations (${recs.length})`} />
          <ul className="space-y-3">
            {recs.map((r) => (
              <li
                key={r.playbook_item_id}
                className="rounded-md border border-slate-200 p-3"
                data-testid="gap-recommendation-item"
              >
                <div className="mb-1 flex items-center gap-2">
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${
                      r.priority === 'critical'
                        ? 'bg-red-100 text-red-800'
                        : r.priority === 'high'
                          ? 'bg-orange-100 text-orange-800'
                          : r.priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {r.priority}
                  </span>
                  <span className="text-xs font-mono text-slate-500">{r.gap_type}</span>
                  {/*
                    The kind is what the consultant should actually do, so it
                    always shows. The content type describes the shape of a
                    page to build and only exists for a new page — rendering
                    it unconditionally used to throw and take the whole list
                    down with it.
                  */}
                  {[
                    kindLabel(r.recommendation_kind) ? (
                      <span
                        key="kind"
                        data-testid="gap-recommendation-kind"
                        className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700"
                      >
                        {kindLabel(r.recommendation_kind)}
                      </span>
                    ) : null,
                    r.recommended_content_type ? (
                      <span key="ctype" className="text-xs text-slate-500">
                        {r.recommended_content_type.replace(/_/g, ' ')}
                      </span>
                    ) : null,
                    <span key="gain" className="text-xs text-slate-500">
                      gain ~{(r.estimated_coverage_gain * 100).toFixed(0)}%
                    </span>,
                  ]
                    .filter((node): node is ReactElement => node !== null)
                    .map((node, i) => (
                      <Fragment key={node.key ?? i}>
                        <span className="text-xs text-slate-400">•</span>
                        {node}
                      </Fragment>
                    ))}
                </div>

                {r.cluster_stale && (
                  <div
                    data-testid="cluster-stale-banner"
                    className="mb-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800"
                  >
                    <Icon name="bx-error" />
                    Referenced cluster has shifted — re-analyze to refresh.
                  </div>
                )}

                <p className="text-sm text-slate-700 whitespace-pre-line">{r.rationale}</p>

                <div className="mt-2 flex items-center gap-2">
                  <Link
                    to={`/playbooks/${r.playbook_id}?source_type=coverage_analysis&analysis_id=${analysis.id}`}
                    className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline"
                    data-testid="playbook-link"
                  >
                    <Icon name="bx-link-external" />
                    Open in playbook UI
                  </Link>
                  <span className="text-xs text-slate-400">
                    cluster: {r.fanout_cluster_slug}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </Region>
      )}

      <StallDiagnostics
        analysis={{
          ...analysis,
          partial_completion_reason: analysis.partial_completion_reason ?? null,
        }}
        isTerminal={isTerminal}
      />
    </div>
  );
}
